import { MODULE_REGISTRY, type ModuleDefinition } from "@/config/modules";
import { APP_REGISTRY } from "./OsAppRegistry";

/**
 * OS-only launcher entries with no MODULE_REGISTRY row (or no module gate).
 * Operations hub only — individual ops-* tools stay in OperationsHub, not this grid.
 */
const OS_POPOVER_EXTRA_APP_IDS = ["ops", "codex", "minesweeper"] as const;

/**
 * Permission modules for deep-linked windows (e.g. open a portfolio from
 * screener) — not standalone apps in the Apps grid.
 */
const OS_POPOVER_SKIP_MODULE_IDS = new Set(["portfolio", "product"]);

/**
 * Maps a module definition to the OS app id used in APP_REGISTRY and the dock.
 */
export function moduleToOsAppId(module: ModuleDefinition): string {
  return module.osAppId ?? module.routePath.replace(/^\//, "");
}

/**
 * Keep Minesweeper last in the Apps popover regardless of base list order.
 *
 * @param appIds - Permission-filtered app ids in display order
 */
export function ensureMinesweeperLast(appIds: string[]): string[] {
  const rest = appIds.filter((id) => id !== "minesweeper");
  return appIds.includes("minesweeper") ? [...rest, "minesweeper"] : rest;
}

function pushUnique(
  ids: string[],
  seen: Set<string>,
  dockSet: Set<string>,
  appId: string,
): void {
  if (dockSet.has(appId) || seen.has(appId)) return;
  seen.add(appId);
  ids.push(appId);
}

/**
 * App ids for the aniOS Apps grid popover.
 *
 * Every MODULE_REGISTRY launcher that is permission-gated in APP_REGISTRY,
 * minus whatever is already on the dock (`excludeAppIds`). Classic nav's
 * `showInAppsSection` flag does not apply here — dock vs Apps is the split.
 *
 * @param excludeAppIds - App ids already on the dock; omitted from the grid.
 */
export function getOsAppsPopoverAppIds(excludeAppIds: string[] = []): string[] {
  const excluded = new Set(excludeAppIds);
  const seen = new Set<string>();
  const ids: string[] = [];

  for (const module of Object.values(MODULE_REGISTRY)) {
    if (module.category === "utility") continue;
    if (OS_POPOVER_SKIP_MODULE_IDS.has(module.id)) continue;
    const appId = moduleToOsAppId(module);
    const app = APP_REGISTRY[appId];
    if (!app?.module) continue;
    pushUnique(ids, seen, excluded, appId);
  }

  for (const appId of OS_POPOVER_EXTRA_APP_IDS) {
    pushUnique(ids, seen, excluded, appId);
  }

  return ensureMinesweeperLast(ids);
}
